import {DecimalPipe} from "@angular/common"
import {
    AfterViewInit,
    Component,
    ElementRef,
    HostListener,
    input,
    viewChild,
    output,
    inject,
    signal,
    computed,
    effect,
    model,
    DestroyRef,
    ChangeDetectionStrategy,
} from "@angular/core"
import {fromEvent, Subscription} from "rxjs"
import {ScalarSocketRange} from "@socket-editor/types"
import {PreviewSceneService} from "@material-editor/services/preview-scene.service"
import {takeUntilDestroyed} from "@angular/core/rxjs-interop"

@Component({
    changeDetection: ChangeDetectionStrategy.OnPush,
    selector: "cm-scalar-input-socket",
    templateUrl: "./scalar-input-socket.component.html",
    styleUrls: ["./scalar-input-socket.component.scss"],
    imports: [DecimalPipe],
})
export class ScalarInputSocketComponent implements AfterViewInit {
    readonly $bar = viewChild.required<ElementRef<HTMLDivElement>>("bar")
    readonly $barFill = viewChild.required<ElementRef<HTMLDivElement>>("barFill")
    readonly $valueInput = viewChild.required<ElementRef<HTMLInputElement>>("valueInput")
    readonly $label = input.required<string>({alias: "label"})
    readonly $type = input.required<string>({alias: "type"})
    readonly $disableEditingIfConnected = input<boolean>(true, {alias: "disableEditingIfConnected"})

    readonly previewSceneService = inject(PreviewSceneService)
    private readonly destroyRef = inject(DestroyRef)
    readonly $connected = input(false, {alias: "connected"})

    readonly $range = input<ScalarSocketRange | undefined>(undefined, {alias: "range"})

    private readonly $_rangeMaxSet = computed(() => {
        const r = this.$range()
        return !!(r && "max" in r)
    })
    private readonly $_rangeMinSet = computed(() => {
        const r = this.$range()
        return !!(r && "min" in r)
    })
    readonly $_rangeSet = computed(() => this.$_rangeMaxSet() && this.$_rangeMinSet())

    readonly $value = model<number | undefined>(undefined, {alias: "value"})

    readonly valueChange = output<number | undefined>()

    readonly $hideBarFill = input(false, {alias: "hideBarFill"})
    readonly $barFillWidth = signal(25)
    barWidth?: number

    startValue: number | undefined = this.$value()
    startX?: number
    dragActive = false

    readonly $editMode = signal<"bar" | "input">("bar")
    mouseMoveSubscription: Subscription | null = null

    constructor() {
        effect(() => {
            const value = this.$value()
            this.applyValue(value)
        })
    }

    private applyValue(value: number | undefined): void {
        this.$value.set(value)
        this.updateBar()
        this.previewSceneService.$refresh.next()
    }

    @HostListener("document:mouseup", ["$event"])
    onMouseUp(event: MouseEvent) {
        this.dragEnd(event)
        if (this.$editingEnabled() && event.target !== this.$valueInput().nativeElement) {
            this.$editMode.set("bar")
        }
    }

    readonly $editingEnabled = computed(() => {
        return !(this.$connected() && this.$disableEditingIfConnected())
    })

    ngAfterViewInit(): void {
        fromEvent<MouseEvent>(this.$bar().nativeElement, "mousedown")
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe((event) => this.dragStart(event))
        fromEvent<MouseEvent>(this.$bar().nativeElement, "click")
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe((event) => this.activateInputEditMode(event))
        // Prevent dragging of the node when trying to select the input's value.
        fromEvent<MouseEvent>(this.$valueInput().nativeElement, "mousedown")
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe((event) => event.stopPropagation())
        fromEvent<KeyboardEvent>(this.$valueInput().nativeElement, "keydown")
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe((event) => this.commitInputValue(event))
    }

    onValueChange(value: number) {
        if (value == null || Number.isNaN(value)) {
            this.applyValue(undefined)
            this.valueChange.emit(undefined)
            return
        }
        const range = this.$range()
        if (this.$_rangeMinSet() && range && value < range.min!) value = range.min!
        else if (this.$_rangeMaxSet() && range && range.max! < value) value = range.max!
        else value = Math.round(value * 1000) / 1000
        this.applyValue(value)
        this.valueChange.emit(value)
    }

    dragStart(event: MouseEvent): void {
        this.mouseMoveSubscription = fromEvent<MouseEvent>(document.body, "mousemove")
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe((event) => {
                if (!this.$editingEnabled()) return
                this.dragValue(event)
            })
        this.startX = event.clientX
        this.startValue = this.$value()
        this.hideCursor()
        this.dragActive = true
        const barBoundingRect: DOMRect = this.$bar().nativeElement.getBoundingClientRect()
        this.barWidth = barBoundingRect.width
        event.stopPropagation()
    }

    dragValue(event: MouseEvent): void {
        if (!this.dragActive) return
        event.stopPropagation()
        const rangeMax = this.$_rangeMaxSet() ? (this.$range()?.max ?? 1.0) : 1.0
        this.onValueChange(this.startValue! + ((event.clientX - this.startX!) / this.barWidth!) * rangeMax)
    }

    updateBar(): void {
        const range = this.$range()
        if (this.$_rangeSet() && range) {
            const value = this.$value()
            if (value === undefined) this.$barFillWidth.set(0)
            else this.$barFillWidth.set(((value - range.min!) / (range.max! - range.min!)) * 100)
        }
    }

    dragEnd(_event: MouseEvent): void {
        if (this.dragActive) {
            this.mouseMoveSubscription?.unsubscribe()
            this.showCursor()
            this.dragActive = false
            // Only emit if value actually changed during drag
            if (this.$value() !== this.startValue) {
                this.valueChange.emit(this.$value())
            }
        }
    }

    hideCursor(): void {
        document.body.style.cursor = "none"
    }

    showCursor(): void {
        document.body.style.cursor = "auto"
    }

    activateInputEditMode(event: MouseEvent): void {
        if (event.clientX !== this.startX) return
        this.$editMode.set("input")
        setTimeout(() => {
            this.$valueInput().nativeElement.focus()
            this.$valueInput().nativeElement.select()
        }, 0)
    }

    commitInputValue(event: KeyboardEvent): void {
        // Prevent backspace from bubbling up and deleting nodes
        if (event.code === "Backspace") {
            event.stopPropagation()
            return
        }

        if (event.code !== "Enter" && event.code !== "NumpadEnter") return
        this.$valueInput().nativeElement.blur()
        this.$editMode.set("bar")
    }
}
